import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  revokeWallboard,
  saveWallboard,
  wallboardsQuery,
  type WallboardDraft,
  type WallboardsScreen,
} from '../../api/admWallboards'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { Forbidden } from '../../ui/Forbidden'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { SettingsCheck } from '../../ui/SettingsParts'

/**
 * ADM -> Displays, din `views/version-3/softsee/adm/dashboard/wallboards.php`.
 *
 * CE E. Un ecran pentru o camera de operatiuni: cifre mari, care se
 * improspateaza singure, fara nimic de apasat. Se deschide FARA autentificare,
 * ca sa nu trebuiasca sa ramana cineva logat peste noapte - deci adresa insasi
 * e cheia, si de-aia e lunga si de-aia se poate retrage.
 *
 * ADRESA SE ARATA INTREAGA SI SE POATE SELECTA, fiindca felul in care una din
 * astea ajunge pe un televizor e cineva care o copiaza intr-un mesaj sau o
 * tasteaza. Ascunsa in spatele unui buton de copiere care cade tacut pe un
 * browser vechi, ar face lucrul de nefolosit exact in camerele pentru care e.
 *
 * ALEGATORUL DE CIFRE SE SCHIMBA CU VEDEREA, fiindca cele trei vederi n-au
 * nimic in comun: „scanari care ruleaza" nu inseamna nimic pe o tabla de
 * sanatate, si „schimbari neautorizate" nu inseamna nimic pe una de
 * descoperire. Toate listele vin intr-un singur raspuns si se arata una -
 * sunt douasprezece bife, iar o cerere ca sa le redesenezi ar fi mai inceata
 * decat omul care apasa.
 *
 * PAGINA, NU FEREASTRA. In portal era un fragment incarcat intr-un modal de pe
 * tabloul de descoperire. Aici e o pagina la adresa ei, `/adm/wallboards`: are
 * un formular cu paisprezece controale si un tabel, ceea ce nu e continut de
 * fereastra, si asa poate fi si data mai departe ca legatura. Nu are rand in
 * `MOVED`: nicio bara de modul nu duce la ea.
 */

const STYLES = ['css/adm.css']

/** Ce are o tabla noua inainte sa atinga cineva ceva. */
function emptyDraft(screen: WallboardsScreen | undefined): WallboardDraft {
  const view = 1
  return {
    ID: 0,
    name: '',
    view,
    /* Firma se alege dintre ale omului. Cand are una singura, aia e; „toate
       firmele" (0) se ofera numai cui chiar le vede pe toate. */
    company_id: screen?.canPickEveryCustomer ? 0 : (screen?.companies[0]?.id ?? 0),
    tiles: screen?.defaults[String(view)] ?? [],
    chart: '',
    theme: 'dark',
    refresh_seconds: 30,
  }
}

/**
 * Adresa unei table, scrisa intr-un singur loc.
 *
 * O scriu doua lucruri - campul din care omul o copiaza cu mana pe un televizor,
 * si butonul „Open" - si trebuie sa fie ACEEASI: daca ar fi doua sfori, cineva
 * ar copia una si ar proba cealalta.
 *
 * Calea e cea din portal (`monitor/wallboard/<jeton>`), fiindca jetonul e
 * cheia si ea nu se schimba cand o tabla se redenumeste. Ce s-a schimbat e
 * radacina sub care sta aplicatia - `BASE_URL` e `/app/` (vezi `base` din
 * vite.config.ts). Pana azi randul asta arata spre portalul vechi, care nu mai
 * exista pe server: adresa era scrisa corect si nu raspundea nimeni la ea.
 */
export function wallboardAddress(token: string): string {
  return `${window.location.origin}${import.meta.env.BASE_URL}monitor/wallboard/${token}`
}

export function WallboardsPage() {
  const shell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const screen = useQuery(wallboardsQuery())
  const client = useQueryClient()

  const [draft, setDraft] = useState<WallboardDraft>(() => emptyDraft(undefined))
  const [note, setNote] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null)
  const [started, setStarted] = useState(false)

  const page = screen.data

  /* Cand raspunsul soseste, formularul gol se aseaza pe ce ofera serverul -
     firma omului si cifrele implicite ale vederii. Numai o data: dupa ce omul
     a inceput sa scrie, nu i se mai schimba nimic sub degete. */
  useEffect(() => {
    if (!page || started) return
    setDraft(emptyDraft(page))
  }, [page, started])

  const change = (patch: Partial<WallboardDraft>) => {
    setStarted(true)
    setDraft((d) => ({ ...d, ...patch }))
  }

  const catalogue = useMemo(
    () => page?.catalogues[String(draft.view)] ?? [],
    [page, draft.view],
  )
  const charts = useMemo(() => page?.charts[String(draft.view)] ?? [], [page, draft.view])

  const maxTiles = page?.maxTiles ?? 8
  const full = draft.tiles.length >= maxTiles

  const save = useMutation({
    mutationFn: () => saveWallboard(draft),
    onSuccess: (out) => {
      if (out.status !== 'OK') {
        setNote({ kind: 'bad', text: out.message })
        return
      }
      setNote({ kind: 'ok', text: out.message })
      setStarted(false)
      void client.invalidateQueries({ queryKey: ['adm', 'wallboards'] })
    },
    onError: (error: Error) => setNote({ kind: 'bad', text: error.message }),
  })

  const withdraw = useMutation({
    mutationFn: (id: number) => revokeWallboard(id),
    onSuccess: (out) => {
      setNote({ kind: out.status === 'OK' ? 'ok' : 'bad', text: out.message })
      void client.invalidateQueries({ queryKey: ['adm', 'wallboards'] })
    },
    onError: (error: Error) => setNote({ kind: 'bad', text: error.message }),
  })

  if (screen.isError) {
    const status = (screen.error as { status?: number }).status
    if (status === 403) return <Forbidden />
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 16 }}>
          <i className="fas fa-exclamation-triangle"></i> {(screen.error as Error).message}
        </div>
      </Content>
    )
  }

  /*
   * O cifra se scoate cand se apasa a doua oara. La opt, cele nealese se sting
   * - dezactivate, nu primite si aruncate pe tacute: o bifa care se bifeaza si
   * apoi nu apare pe perete e o eroare din locul in care sta omul. Cele deja
   * alese raman vii, deci felul de a schimba una e s-o scoti.
   */
  const toggleTile = (key: string) => {
    const picked = draft.tiles.includes(key)
    if (!picked && full) return
    change({ tiles: picked ? draft.tiles.filter((k) => k !== key) : [...draft.tiles, key] })
  }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-hoki"
          icon="fa-tv"
          title="Displays"
          note="a screen for an operations room: large figures, refreshing on their own, nothing to click"
        />

        <p className="text-muted">
          It opens without a login, so nothing has to stay logged in overnight — the address itself is the key,
          which is why it is long and why it can be withdrawn.
          <br />
          <br />
          Treat one like a password: anybody holding the address sees that customer&apos;s estate.
        </p>

        {note ? (
          <div className={`alert ${note.kind === 'ok' ? 'alert-success' : 'alert-warning'}`} id="wbNote">
            <i className={`fas ${note.kind === 'ok' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>{' '}
            {note.text}
          </div>
        ) : null}

        {page ? (
          <div className="adm-card adm-tone-blue-hoki wbManager">
            <div className="adm-card-head">
              <h5>
                <i className="fas fa-plus"></i> New display
              </h5>
              <span className="adm-card-note">
                pick up to {maxTiles} figures — more than that and each one is too small to read across a room
              </span>

              <div className="wbTheme">
                <label htmlFor="wbTheme">Theme</label>
                {/* Hotarata aici si nu luata din setarile ecranului: ce
                    conteaza e lumina din camera, iar un televizor nu poate
                    raporta asa ceva. */}
                <select
                  className="form-control input-sm"
                  id="wbTheme"
                  value={draft.theme}
                  onChange={(e) => change({ theme: e.target.value })}
                >
                  {page.themes.map((theme) => (
                    <option key={theme.key} value={theme.key}>
                      {theme.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 form-group">
                <label htmlFor="wbName">Name</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  id="wbName"
                  maxLength={120}
                  placeholder="Operations room"
                  value={draft.name}
                  onChange={(e) => change({ name: e.target.value })}
                />
              </div>

              <div className="col-md-3 form-group">
                <label htmlFor="wbView">Shows</label>
                <select
                  className="form-control input-sm"
                  id="wbView"
                  value={String(draft.view)}
                  onChange={(e) => {
                    const view = Number(e.target.value)
                    /* Cifrele se schimba odata cu vederea: cele vechi n-au
                       inteles pe cea noua, si pastrate ar fi fost oricum
                       aruncate de server. */
                    change({ view, tiles: page.defaults[String(view)] ?? [], chart: '' })
                  }}
                >
                  {page.views.map((view) => (
                    <option key={view.key} value={view.key}>
                      {view.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 form-group">
                <label htmlFor="wbCompany">Customer</label>
                <select
                  className="form-control input-sm"
                  id="wbCompany"
                  value={String(draft.company_id)}
                  onChange={(e) => change({ company_id: Number(e.target.value) })}
                >
                  {/* „Toate firmele" e parcul INTREG al furnizorului, deci se
                      ofera numai cui chiar il vede pe tot. */}
                  {page.canPickEveryCustomer ? <option value="0">Every customer</option> : null}
                  {page.companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-2 form-group">
                <label htmlFor="wbRefresh">Refresh</label>
                <select
                  className="form-control input-sm"
                  id="wbRefresh"
                  value={String(draft.refresh_seconds)}
                  onChange={(e) => change({ refresh_seconds: Number(e.target.value) })}
                >
                  <option value="15">15 s</option>
                  <option value="30">30 s</option>
                  <option value="60">1 min</option>
                  <option value="300">5 min</option>
                </select>
              </div>
            </div>

            <div className="wbTiles" data-view={draft.view}>
              <label className="wbTilesLabel">Figures on the wall</label>

              <div className="wbTileGrid">
                {catalogue.map((tile) => {
                  const picked = draft.tiles.includes(tile.key)
                  return (
                    <div
                      key={tile.key}
                      className={`adm-policy wbTile${!picked && full ? ' is-full' : ''}`}
                    >
                      <SettingsCheck
                        value={tile.key}
                        checked={picked}
                        disabled={!picked && full}
                        onChange={() => toggleTile(tile.key)}
                        label={tile.label}
                      />
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="wbCharts">
              <label className="wbTilesLabel" htmlFor="wbChart">
                Graph under the figures
              </label>
              {/* Unul singur pe tabla: asta se citeste dintr-o camera, si doua
                  grafice alaturate injumatatesc latimea fiecaruia. */}
              <select
                className="form-control input-sm wbChartPick"
                id="wbChart"
                value={draft.chart}
                onChange={(e) => change({ chart: e.target.value })}
              >
                {charts.map((chart) => (
                  <option key={chart.key} value={chart.key}>
                    {chart.label}
                  </option>
                ))}
              </select>
            </div>

            <p className="wbCount text-muted small" id="wbCount">
              {draft.tiles.length} of {maxTiles} chosen
              {full ? ' — untick one to swap it' : ''}
            </p>

            <button
              className="btn btn-primary btn-sm"
              id="wbCreate"
              disabled={save.isPending || draft.name.trim() === ''}
              onClick={() => save.mutate()}
            >
              <i className={save.isPending ? 'fas fa-spinner fa-spin' : 'fas fa-link'}></i> Create the link
            </button>
          </div>
        ) : null}

        {page && page.boards.length === 0 ? (
          <div className="adm-empty-inline" id="wbEmpty">
            <i className="fas fa-tv"></i> No display has been created yet.
          </div>
        ) : null}

        {page && page.boards.length > 0 ? (
          <table className="table table-hover adm-list" id="wbList">
            <thead>
              <tr>
                <th>Name</th>
                <th>Shows</th>
                <th>Theme</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Last opened</th>
                <th className="text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {page.boards.map((board) => (
                <tr key={board.id}>
                  <td>{board.name}</td>
                  <td>{board.viewLabel}</td>
                  <td className="adm-muted">{board.themeLabel}</td>
                  <td>{board.companyName === '' ? 'Every customer' : board.companyName}</td>
                  <td>
                    {/* Intreaga si selectabila: asa ajunge pe un televizor. */}
                    <input
                      type="text"
                      className="form-control input-sm wallboardUrl"
                      readOnly
                      value={wallboardAddress(board.token)}
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                  </td>
                  <td className="adm-nowrap adm-muted">
                    {board.lastSeenAt === '' ? 'never' : board.lastSeenAt}
                  </td>
                  <td className="text-right adm-nowrap">
                    {/*
                      „Open", ca in portal. N-a fost desenat la mutarea emiterii
                      fiindca afisajul inca nu raspundea pe platforma noua; acum
                      raspunde, deci butonul se intoarce la locul lui.

                      Se deschide in FILA NOUA: cine se uita la lista are de
                      obicei mai multe table de verificat, si o tabla e o pagina
                      pe care o lasi deschisa, nu una prin care treci.

                      Si se deschide LA FEL ca pe un televizor, desi omul care
                      apasa E logat: pagina sare scheletul si isi cere cifrele
                      fara cookie (`credentials: 'omit'`), deci ce vede aici e
                      exact ce va vedea camera - nu o versiune care merge doar
                      fiindca s-a intamplat sa fie cineva logat.
                    */}
                    <a
                      className="btn btn-info btn-xs wallboardOpen"
                      href={wallboardAddress(board.token)}
                      target="_blank"
                      rel="noopener"
                    >
                      <i className="fas fa-external-link-alt"></i> Open
                    </a>{' '}
                    <button
                      className="btn btn-danger btn-xs wallboardRevoke"
                      data-id={board.id}
                      title="The address stops working immediately, and the display leaves this list"
                      disabled={withdraw.isPending}
                      onClick={() => withdraw.mutate(board.id)}
                    >
                      <i className="fas fa-ban"></i> Withdraw
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </Content>
  )
}
